/**
 * @fileoverview Console API Client
 *
 * Handles WebSocket token generation for VM console access.
 * Supports both authenticated users (Okta) and external share users.
 *
 * ARCHITECTURE:
 * - Two-step connection: First get token from central API, then connect to regional WebSocket
 * - Central API returns: { ws_token, ws_url, expires_in }
 * - Frontend connects to regional WebSocket with token
 * - Backend validates token and proxies to guacd
 */

import client from './client'

/**
 * Get WebSocket token for authenticated VM console access
 *
 * @param {string} vmId - VM identifier (prefixed format: vm-xxx)
 * @returns {Promise<{ws_token: string, ws_url: string, expires_in: number}>}
 * @throws {Error} If token generation fails
 * @example
 * const { ws_token, ws_url } = await getConsoleToken('vm-abc123')
 * const wsUrl = `${ws_url}?token=${ws_token}`
 */
export async function getConsoleToken(vmId) {
  const response = await client.post(`/v1/console/${vmId}/token`)
  return response.data
}

/**
 * Get WebSocket token for share portal console access
 *
 * @param {string} shareId - Share identifier (UUID format)
 * @param {string} vmId - VM identifier (prefixed format: vm-xxx)
 * @param {string} sessionToken - Share session token from sessionStorage
 * @returns {Promise<{ws_token: string, ws_url: string, expires_in: number}>}
 * @throws {Error} If token generation fails or session is invalid
 * @example
 * const token = getShareSessionToken()
 * const { ws_token, ws_url } = await getShareConsoleToken(shareId, vmId, token)
 * const wsUrl = `${ws_url}?token=${ws_token}`
 */
export async function getShareConsoleToken(shareId, vmId, sessionToken) {
  const response = await client.post(`/v1/shares/${shareId}/vms/${vmId}/console/token`, null, {
    headers: { 'X-Share-Session': sessionToken }
  })
  return response.data
}
